import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import CheckBox from 'components/final-form/CheckBox';
import Input from 'components/final-form/Input';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid } from 'semantic-ui-react';
import { addContainer, editContainer } from 'services/selfServiceContainerServices';
import styled from 'styled-components';
import { UpsertSelfServiceContainerDto } from 'ts-types/api.types';
import { required } from 'utils/validatorUtils';

const UpsertSelfServiceContainerFormContent = styled(FormContent)`
  max-width: 570px;
`;

interface Props extends MessageSourceProps, AuthConsumerRenderProps {
  toggleAddEditModal: () => void,
  onCancel: () => void,
  onSave: () => void,
  container?: UpsertSelfServiceContainerDto,
  containerId?: string
}

interface State {
  errorMessage?: string,
  cancelTokenSource: CancelTokenSource
}

class UpsertSelfServiceContainerForm extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      cancelTokenSource
    }
  }

  handleSubmit = async (values: Partial<UpsertSelfServiceContainerDto>) => {

    let saveValues: Partial<UpsertSelfServiceContainerDto> = {...values};

    const {containerId, getMessage, onSave, currentUser} = this.props;
    const {cancelTokenSource} = this.state;
    const mandateId = currentUser!.userConfig.mandateId;

    const handleError = () => {
      this.setState({
        errorMessage: getMessage("error.general"),
        cancelTokenSource
      })
    };

    if (!containerId) {
      saveValues.mandateId = mandateId;
    }

    if (containerId) {
      await editContainer(containerId, saveValues, cancelTokenSource)
          .then(onSave)
          .catch(handleError)
    } else {
      await addContainer(saveValues, cancelTokenSource)
          .then(onSave)
          .catch(handleError)
    }
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  initialContainerValues = (): Partial<UpsertSelfServiceContainerDto> => {
    return {
      testSchedulingEnabled: false,
      selfOnboardEnabled: true,
      callCenterSupported: false
    };
  }

  render(): React.ReactNode {

    const {container = this.initialContainerValues()} = this.props;

    return (
        <FinalForm
            onSubmit={(values) => this.handleSubmit(values)}
            initialValues={container}
            subscription={{pristine: true, submitting: true}}
            render={this.renderForm}
        />
    );
  }

  renderForm = ({handleSubmit, submitting}: FormRenderProps): React.ReactNode => {

    const {getMessage, onCancel, containerId} = this.props;
    const {errorMessage} = this.state;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <UpsertSelfServiceContainerFormContent>
              {
                errorMessage &&
                <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                  {errorMessage}
                </StyledErrorMessage>
              }

              <PraxFormGrid>
                <Grid.Row>
                  <Grid.Column width={13}>
                  </Grid.Column>
                  <Grid.Column width={3} textAlign="right">
                    <Field
                        name="active"
                        component={CheckBox}
                        label={`${getMessage("active")}`}
                        toggle
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("container.id")} :
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        fluid
                        name="id"
                        component={Input}
                        validate={required}
                        autoFocus
                        disabled={containerId !== undefined}
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("container.description")} :
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        fluid
                        name="description"
                        component={Input}
                        validate={required}
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("container.type")} :
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        fluid
                        name="type"
                        component={Input}
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("container.textResourcesSuffix")} :
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        fluid
                        name="textResourcesSuffix"
                        component={Input}
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("container.selfOnboardEnabled")} :
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        id="selfOnboardEnabled"
                        name="selfOnboardEnabled"
                        component={CheckBox}
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("container.enableInstitutionLogin")} :
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        id="enableInstitutionLogin"
                        name="enableInstitutionLogin"
                        component={CheckBox}
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("container.enableParticipantLogin")} :
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        id="enableParticipantLogin"
                        name="enableParticipantLogin"
                        component={CheckBox}
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("container.testSchedulingEnabled")} :
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        id="testSchedulingEnabled"
                        name="testSchedulingEnabled"
                        component={CheckBox}
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("container.callCenterSupported")} :
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        id="callCenterSupported"
                        name="callCenterSupported"
                        component={CheckBox}
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("container.paymentsSupported")} :
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        id="paymentsSupported"
                        name="paymentsSupported"
                        component={CheckBox}
                    />
                  </Grid.Column>
                </Grid.Row>
              </PraxFormGrid>
            </UpsertSelfServiceContainerFormContent>

            <FormButtonsRow>
              <Button compact type="submit" primary disabled={submitting}>{getMessage("button.save")}</Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCancel}>{getMessage("button.cancel")}</Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    );
  };

}

export default withMessages(withAuthContext(UpsertSelfServiceContainerForm));
